import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import AuditLogCard from "./AuditLogCard";
import { auditsGetRequest } from "store/audits/action";
import AuditLogCardSkeleton from "./AuditLogCardSkeleton";
import { ActivityEntry, HourlyActivityLog } from "store/audits/types";
import { withTranslation } from "react-i18next";

const Logs: React.FC = ({ props, t }: any) => {
    const { id } = useParams();
    const location = useLocation();

    // Extract date from query string
    const searchParams = new URLSearchParams(location.search);
    const dateParam = searchParams.get("date");
    const dispatch: any = useDispatch();

    const audits = useSelector((state: ApplicationState) => state.audits.details);
    const loading = useSelector((state: ApplicationState) => state.audits.loading);
    const hourly_logs = useMemo<HourlyActivityLog[]>(() => {
        return audits?.hourly_logs || [];
    }, [audits?.hourly_logs]);
    const remarks: string[] = audits?.remarks || [];

    const summary = useMemo(() => {
        const defaultMetrics = { total_points: 0, total_leads: 0, total_activities: 0 };
        return {
            individual: audits?.summary?.individual || defaultMetrics,
            team: audits?.summary?.team || defaultMetrics,
        };
    }, [audits?.summary]);

    const user = audits?.user || { name: "–" };

    const [selectedAction, setSelectedAction] = useState<string>("");
    const [selectedChildModel, setSelectedChildModel] = useState<string>("");
    const [selectedModel, setSelectedModel] = useState<string>("");
    const [uniqueOnly, setUniqueOnly] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Date>(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        if (dateParam) {
            const parsed = new Date(dateParam + "T00:00:00");
            if (!isNaN(parsed.getTime())) {
                parsed.setHours(0, 0, 0, 0); // Normalize to start of day
                return parsed;
            }
        }
        return now;
    });

    // function to dispatch the fetch
    const loadData = useCallback(() => {
		const startOfDay = new Date(selectedDate);
		startOfDay.setHours(0, 0, 0, 0);

		const endOfDay = new Date(selectedDate);
		endOfDay.setHours(23, 59, 59, 999);

        const filter = {
            quick: [
                {
                    key: "record",
                    keyLabel: "Students",
                    condition: "IN",
                    conditionLabel: "is any of",
                    values: [id],
                    quick: true,
                },
                {
                    key: "created_at",
                    keyLabel: "Audit Date",
                    condition: "BETWEEN",
                    conditionLabel: "between",
                    value: startOfDay.toISOString(),
                    highValue: endOfDay.toISOString(),
                    quick: true,
                },
            ],
            advance: [],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        dispatch(auditsGetRequest(filter));
    }, [dispatch, selectedDate]);


    // initial + on date change
    useEffect(() => {
        loadData();
    }, [loadData]);

    const filterOptions = useMemo(() => {
        const actions = new Set<string>();
        const models = new Set<string>();
        const childModels = new Set<string>();

        hourly_logs.forEach((log: HourlyActivityLog) => {
            log.logs.forEach((entry: ActivityEntry & { model?: string; child_model?: string; action?: string }) => {
                if (entry.action) actions.add(entry.action);
                if (entry.model) models.add(entry.model); // Primary Model
                if (entry.child_model) childModels.add(entry.child_model); // Related Module
            });
        });

        return {
            actions: Array.from(actions).sort(),
            models: Array.from(models).sort(),           // Primary Model dropdown
            childModels: Array.from(childModels).sort(), // Related Module dropdown
        };
    }, [hourly_logs]);

    const filteredLogs = useMemo(() => {
        const seenRecords = new Set<string>();
        return hourly_logs
            .map((log) => ({
                ...log,
                logs: log.logs.filter((entry: any) => {
                    if (uniqueOnly) {
                        if (!entry.record) return false;
                        if (seenRecords.has(entry.record)) return false;
                        seenRecords.add(entry.record);
                    }

                    const matchesAction = !selectedAction || entry.action === selectedAction;
                    const matchesModel = !selectedModel || entry.model === selectedModel;
                    const matchesChildModel = !selectedChildModel || entry.child_model === selectedChildModel;

                    return matchesAction && matchesModel && matchesChildModel;
                }),
            }))
            .filter((log) => log.logs.length > 0);
    }, [hourly_logs, selectedAction, selectedModel, selectedChildModel, uniqueOnly]);


    const allTimestamps = useMemo(() => {
        return hourly_logs.flatMap((log) =>
            log.logs.map((entry) =>
                new Date(entry.timestamp.replace(/\[.*\]$/, "")).getTime()
            )
        );
    }, [hourly_logs]);

    const [checkInTime, checkOutTime] = useMemo(() => {
        if (!allTimestamps.length) return ["–", "–"];
        const minTime = new Date(Math.min(...allTimestamps));
        const maxTime = new Date(Math.max(...allTimestamps));
        const formatTime = (date: Date) =>
            date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        return [formatTime(minTime), formatTime(maxTime)];
    }, [allTimestamps]);

    return (

        <React.Fragment>
            {loading ? (
                <AuditLogCardSkeleton />
            ) : (
                <AuditLogCard
                    staffName={user?.name || "–"}
                    shift={"09:00 - 18:00"} // can be derived dynamically if available
                    remarks={remarks}
                    summary={summary}
                    hourlyLogs={filteredLogs}
                    selectedAction={selectedAction}
                    selectedModel={selectedModel}
                    selectedChildModel={selectedChildModel}
                    uniqueOnly={uniqueOnly}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    onActionChange={setSelectedAction}
                    onModelChange={setSelectedModel}
                    onChildModelChange={setSelectedChildModel}
                    onToggleUniqueOnly={setUniqueOnly}
                    onRefresh={loadData}
                    actionOptions={filterOptions.actions}
                    modelOptions={filterOptions.models}
                    childModelOptions={filterOptions.childModels}
                    checkInTime={checkInTime}
                    checkOutTime={checkOutTime}
                />
            )}
        </React.Fragment>
    );
};

export default withTranslation()(Logs);